import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useLanguage } from '../../i18n/LanguageContext';
import { HotelIcon } from '../../utils/IconWrapper';
import Section from '../../components/ui/Section';
import Button from '../../components/ui/Button';
import { Helmet } from 'react-helmet';

const PageContainer = styled.div`
  padding-top: 70px;
`;

const HeroSection = styled.div`
  background-image: url('https://images.unsplash.com/photo-1522708323590-d24dbb6b0267?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=crop&w=1350&q=80');
  background-size: cover;
  background-position: center;
  height: 500px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
  }
`;

const HeroContent = styled.div`
  position: relative;
  text-align: center;
  max-width: 800px;
  padding: 0 2rem;
`;

const HeroTitle = styled.h1`
  font-size: 3rem;
  margin-bottom: 1rem;
  color: white;
`;

const HeroSubtitle = styled.p`
  font-size: 1.2rem;
  color: rgba(255, 255, 255, 0.9);
  margin-bottom: 2rem;
`;

const Content = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 2rem;
`;

const ServiceTitle = styled.h2`
  font-size: 2rem;
  margin-bottom: 1.5rem;
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    bottom: -0.5rem;
    left: 0;
    width: 60px;
    height: 3px;
    background-color: var(--accent-primary);
  }
`;

const ServiceDescription = styled.p`
  font-size: 1.1rem;
  color: var(--text-secondary);
  line-height: 1.7;
  margin-bottom: 1.5rem;
`;

const FeatureList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 2rem 0;
`;

const FeatureItem = styled.li`
  display: flex;
  align-items: flex-start;
  margin-bottom: 1.5rem;
  
  svg {
    margin-top: 5px;
    margin-right: 1rem;
    min-width: 24px;
    color: var(--accent-primary);
  }
`;

const FeatureText = styled.div`
  h3 {
    margin-bottom: 0.5rem;
    font-size: 1.2rem;
  }
  
  p {
    color: var(--text-secondary);
    line-height: 1.6;
  }
`;

const CallToAction = styled.div`
  background-color: var(--bg-secondary);
  padding: 3rem;
  text-align: center;
  border-radius: 8px;
  margin: 3rem 0;
`;

const CTATitle = styled.h3`
  font-size: 1.5rem;
  margin-bottom: 1rem;
`;

const CTAText = styled.p`
  color: var(--text-secondary);
  margin-bottom: 2rem;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
`;

const ShortTermRentals: React.FC = () => {
  const { language } = useLanguage();
  
  return (
    <PageContainer>
      <Helmet>
        <title>
          {language === 'it' 
            ? 'Affitti Brevi | Servizi J-O-H' 
            : 'Short-Term Rentals | J-O-H Services'}
        </title>
        <meta 
          name="description" 
          content={language === 'it' 
            ? 'Scopri i nostri servizi di affitto a breve termine: alloggi di lusso per vacanze, viaggi di lavoro e soggiorni brevi in tutta Italia.'
            : 'Discover our short-term rental services: luxury accommodations for holidays, business trips, and short stays throughout Italy.'}
        />
        <meta 
          name="keywords" 
          content={[
            'Short-Term Rentals',
            'Luxury Accommodations',
            'Italy',
            'Vacations',
            'Business Trips',
            'Short Stays',
            'J-O-H Services'
          ].join(', ')}
        />
      </Helmet>
      
      <HeroSection>
        <HeroContent>
          <HeroTitle>
            {language === 'it' ? 'Affitti Brevi' : 'Short-Term Rentals'}
          </HeroTitle>
          <HeroSubtitle>
            {language === 'it'
              ? 'Alloggi di lusso per soggiorni brevi, perfetti per vacanze o viaggi di lavoro.'
              : 'Luxury accommodations for short stays, perfect for vacations or business trips.'}
          </HeroSubtitle>
          <Button variant="primary" to="/contact">
            {language === 'it' ? 'Richiedi Informazioni' : 'Request Information'}
          </Button>
        </HeroContent>
      </HeroSection>
      
      <Section padding="6rem 0">
        <Content>
          <ServiceTitle>
            {language === 'it' ? 'I Nostri Alloggi a Breve Termine' : 'Our Short-Term Accommodations'}
          </ServiceTitle>
          
          <ServiceDescription>
            {language === 'it'
              ? 'I nostri alloggi a breve termine offrono il massimo del comfort e dello stile. Ogni proprietà è accuratamente selezionata e gestita per garantire un\'esperienza impeccabile. Dalla prenotazione al check-out, ci occupiamo di ogni dettaglio per rendere il tuo soggiorno indimenticabile.'
              : 'Our short-term accommodations offer the utmost in comfort and style. Each property is carefully selected and managed to ensure an impeccable experience. From booking to check-out, we take care of every detail to make your stay unforgettable.'}
          </ServiceDescription>
          
          <ServiceDescription>
            {language === 'it'
              ? 'Che tu stia pianificando una vacanza, un viaggio di lavoro o semplicemente hai bisogno di un alloggio temporaneo, le nostre soluzioni sono progettate per soddisfare le tue esigenze con servizi premium e posizioni strategiche.'
              : 'Whether you are planning a vacation, a business trip, or simply need temporary accommodation, our solutions are designed to meet your needs with premium services and strategic locations.'}
          </ServiceDescription>
          
          <FeatureList>
            <FeatureItem>
              <HotelIcon />
              <FeatureText>
                <h3>{language === 'it' ? 'Proprietà Selezionate' : 'Selected Properties'}</h3>
                <p>
                  {language === 'it'
                    ? 'Ogni proprietà nel nostro portafoglio è accuratamente selezionata per garantire elevati standard di qualità, comfort e posizione.'
                    : 'Every property in our portfolio is carefully selected to ensure high standards of quality, comfort, and location.'}
                </p>
              </FeatureText>
            </FeatureItem>
            
            <FeatureItem>
              <HotelIcon />
              <FeatureText>
                <h3>{language === 'it' ? 'Servizio di Concierge' : 'Concierge Service'}</h3>
                <p>
                  {language === 'it'
                    ? 'Il nostro team è disponibile per assistere con richieste speciali, prenotazioni di ristoranti, organizzazione di tour e altro ancora.'
                    : 'Our team is available to assist with special requests, restaurant reservations, tour arrangements, and more.'}
                </p>
              </FeatureText>
            </FeatureItem>
            
            <FeatureItem>
              <HotelIcon />
              <FeatureText>
                <h3>{language === 'it' ? 'Servizi Premium' : 'Premium Services'}</h3>
                <p>
                  {language === 'it'
                    ? 'Dalle pulizie quotidiane alla biancheria di alta qualità, i nostri servizi sono progettati per garantire un soggiorno lussuoso.'
                    : 'From daily cleaning to high-quality linens, our services are designed to ensure a luxurious stay.'}
                </p>
              </FeatureText>
            </FeatureItem>
          </FeatureList>
          
          <CallToAction>
            <CTATitle>
              {language === 'it' ? 'Pronto a Prenotare?' : 'Ready to Book?'}
            </CTATitle>
            <CTAText>
              {language === 'it'
                ? 'Contattaci oggi per scoprire le nostre proprietà disponibili e iniziare a pianificare il tuo soggiorno perfetto.'
                : 'Contact us today to discover our available properties and start planning your perfect stay.'}
            </CTAText>
            <Button variant="primary" to="/contact">
              {language === 'it' ? 'Contattaci Ora' : 'Contact Us Now'}
            </Button>
          </CallToAction>
        </Content>
      </Section>
    </PageContainer>
  );
};

export default ShortTermRentals; 